<template>
  <div class="h-table">
    <div class="action">
      <slot name="action"></slot>
    </div>
    <a-table
      :columns="columns"
      :data-source="list"
      :custom-row="customRow"
      :scroll="{ x: 1300, y: 500 }"
      :loading="loading"
      :row-class-name="customRowClass"
      :row-key="rowKey"
      :pagination="enablePagination ? pagination : false"
      :row-selection="enableSelect ? selection : null"
      :bordered="true"
      :size="size"
      @change="onTableChange"
      @resizeColumn="onResizeColumn"
    >
      <template #headerCell="scope">
        <slot name="header" v-bind="scope">
          <span>{{ scope.title }}</span>
        </slot>
      </template>

      <template #bodyCell="scope">
        <a-tooltip
          placement="topLeft"
          :title="scope.value"
          v-if="scope.column.ellipsis"
        >
          <span class="tbody-td-text">
            <slot v-bind="scope">
              {{ scope.text }}
            </slot>
          </span>
        </a-tooltip>
        <span class="tbody-td-text" v-else>
          <slot v-bind="scope">{{ scope.value }}</slot>
        </span>
      </template>
    </a-table>
  </div>
</template>
<script lang="ts" setup>
import {
  defineProps,
  defineEmits,
  PropType,
  reactive,
  ref,
  defineExpose,
  watch
} from "vue";
import type { IResponseData } from "@/service";
import type { TableColumnType, Table as ATable } from "ant-design-vue";
import type { PaginationType } from "@/types";

type RequestFun = (param: any) => Promise<IResponseData<any>>;
const props = defineProps({
  // 描述列的数组对象
  columns: {
    type: Array as PropType<TableColumnType[]>,
    required: true
  },
  // 表格每行需要绑定的key字段
  rowKey: {
    type: String,
    default: "index"
  },
  // 是否开启选择行
  enableSelect: {
    type: Boolean,
    default: false
  },
  // 开启选择行后 单选多选
  multipleSelect: {
    type: Boolean,
    default: true
  },
  // 请求数据的接口函数
  request: {
    type: Function as PropType<RequestFun>,
    default() {
      return Promise.resolve({
        code: 0,
        message: "请求成功",
        data: []
      });
    }
  },
  // 分页查询的参数
  currentQuery: {
    type: Object,
    default: () => ({})
  },
  appendFields: {
    type: Object,
    default: () => ({})
  },
  enablePagination: {
    type: Boolean,
    default: true
  },
  size: {
    type: String,
    default: "small"
  }
});
const columns = reactive(props.columns);
const emits = defineEmits(["itemClick", "selection"]);
const customRow = (record: any) => {
  return {
    onClick: () => {
      emits("itemClick", record);
    }
  };
};

const loading = ref(true);
const pagination = reactive({
  size: "default",
  responsive: true,
  total: 0,
  current: 1,
  pageSize: 10,
  showTotal(total: number) {
    return `共 ${total} 条`;
  }
});
const list = ref<any[]>([]);
const fetchData = async (params?: any, page?: PaginationType) => {
  if (page) {
    pagination.current = page.current || 1;
    pagination.pageSize = page.pageSize || 10;
  }
  const queryParams = {
    current: props.enablePagination ? pagination.current : undefined,
    size: props.enablePagination ? pagination.pageSize : undefined,
    ...props.currentQuery,
    ...params
  };

  for (const key in queryParams) {
    if (queryParams[key] === "") {
      delete queryParams[key];
    }
  }
  loading.value = true;
  try {
    const res = await props.request(queryParams);
    // console.log("list res", res);
    if (res.code === 200 && res.data) {
      let data: any[] = res.data.records || res.data;
      const total: number = res.data.total || res.data.records.length;

      // 如果要往列表中加入附加参数
      list.value = data.map((item) => ({
        ...item,
        ...props.appendFields
      }));
      pagination.total = total;
    }
  } catch (err) {
    console.log("请求错误", err);
  } finally {
    loading.value = false;
    selection.selectedRowKeys = [];
  }
};
fetchData();

const selection = reactive({
  fixed: true,
  selectedRowKeys: [],
  type: props.multipleSelect ? "checkbox" : "radio",
  onChange(selectedRowKeys: any, selectedRows: any) {
    selection.selectedRowKeys = selectedRowKeys;
    emits("selection", selectedRowKeys, selectedRows);
  }
});

const onTableChange = (page: PaginationType) => {
  pagination.current = page.current;
  pagination.pageSize = page.pageSize;
  selection.selectedRowKeys = [];
  fetchData();
};

const onRefresh = (data?: any) => {
  pagination.current = 1;
  pagination.pageSize = 10;
  selection.selectedRowKeys = [];
  fetchData(data);
};

const onResizeColumn = (width: number, column: TableColumnType) => {
  column.width = width;
};

const customRowClass = (_record: any, index: number) => {
  return index % 2 === 1 ? "table-striped-row" : null;
};
watch(
  () => selection.selectedRowKeys,
  (value) => {
    emits("selection", value);
  }
);
defineExpose({ fetchData, onRefresh });
</script>

<script lang="ts">
export default {
  name: "HTable"
};
</script>

<style lang="less" scoped>
.h-table {
  // margin-top: 20px;
  background-color: #fff;
  padding: 24px;
  border-radius: 3px;
  .action {
    max-height: 40px;
    display: flex;
    align-items: center;
    gap: 24px;
    margin-bottom: 18px;
  }

  /* :deep(.ant-table-thead > tr > th) {
    background-color: #edf1f8;
  } */

  :deep(.ant-table-body) {
    overflow-y: hidden;
    overflow-x: auto;
    &::-webkit-scrollbar {
      width: 0px;
      height: 8px;
    }
    /* 滚动槽 */
    &::-webkit-scrollbar-track {
      border-radius: 10px;
    }
    /* 滚动条滑块 */
    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      background: rgba(0, 0, 0, 0.1);
    }
  }

  .tbody-td-text {
    color: #202d40;
  }
  /* :deep(.table-striped-row > td) {
    background-color: #f8f9fc;
  } */
}
</style>
